import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  addBusinessBankAccount,
  deleteBusinessBankAccount,
  getAllBusinessBankAccounts,
  getBusinessWallet,
  getBanks,
  validateBankAccount,
  updateBusinessBankAccount,
  withdrawRequest,
  getAllTransactionsMetrics,
  getBusinessWalletTransactions,
} from "service";
import { toast } from "sonner";
import { useHfBranch } from ".";
import {
  WithdrawRequestResponse,
  IAddBankFormData,
  IAddBankResponse,
  IBusinessWalletAccountResult,
  IBusinessWalletAccounts,
  IWalletResponse,
  IBanksResponse,
  ValidateBankAccountResponse,
  UpdateBankAccountResponse,
  MetricsResponse,
  IBusinessWalletTransactions,
} from "types";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useModal } from "hooks/useModal";
import { debounce } from "lodash";
import { useWallet } from "hooks/useWallet";

export interface IWithdrawFormData {
  business_bank_account: number | null;
  amount: number;
  saveAsDefault: boolean;
  id: number | null
}


export function useBusinessWallet() {
  const { branch } = useHfBranch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation()

  const [isBusinessBalanceVisible, setIsBusinessBalanceVisible] = useState(() => {
    return localStorage.getItem("isBusinessBalanceVisible") !== "false";
  });

  useEffect(() => {
    localStorage.setItem("isBusinessBalanceVisible", isBusinessBalanceVisible.toString());
  }, [isBusinessBalanceVisible]);

  const [pagination, setPagination] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });

  const [addBankFormData, setAddBankFormData] = useState<IAddBankFormData>({
    account_name: "",
    account_number: "",
    bank_code: "",
    active: true,
  });

  const [withdrawFormData, setWithdrawFormData] = useState<IWithdrawFormData>({
    business_bank_account: null,
    amount: 0,
    saveAsDefault: false,
    id: null
  });

  const [bankValidationData, setBankValidationData] = useState<ValidateBankAccountResponse | null>(null);
  const [selectedBank, setSelectedBank] = useState<IBusinessWalletAccountResult | null>(null);
  const [timeRange, setTimeRange] = useState<string>("Monthly");

  const {
    closeModal: closeDeleteBank,
    openModal: openDeleteBank,
    isOpen: isDeleteBankOpen,
  } = useModal();

  const {
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
    isOpen: isConfirmModalOpen,
  } = useModal();

  const {
    data: businessWallet,
    isPending: businessWalletLoading,
    isError: businessWalletError,
  } = useQuery<IWalletResponse>({
    queryKey: ["business-wallet", branch?.hfId],
    queryFn: async () => {
      if (!branch?.hfId) throw new Error("No branch ID available");
      return await getBusinessWallet(branch.hfId);
    },
    enabled: !!branch?.hfId,
  });

  const {
    data: businessBankAccount,
    isPending: businessBankAccountLoading,
    isError: businessBankAccountError,
    refetch,
  } = useQuery<IBusinessWalletAccounts>({
    queryKey: ["business-bank-accounts", branch?.hfId],
    queryFn: async () => {
      if (!branch?.hfId) throw new Error("No branch ID available");
      return await getAllBusinessBankAccounts(branch.hfId, pagination.current, pagination.pageSize);
    },
    enabled: !!branch?.hfId,
    staleTime: 0,
  });

  const {
    data: businessWalletTransactions,
    isPending: businessWalletTransactionsIsLoading,
    isError: businessWalletTransactionsError,
    refetch: walletRefetch
  } = useQuery<IBusinessWalletTransactions>({
    queryKey: ["business-transactions", branch?.hfId],
    queryFn: async () => {
      if (!branch?.hfId) throw new Error("No branch ID available");
      return await getBusinessWalletTransactions(branch.hfId, pagination.current, pagination.pageSize);
    },
    enabled: !!branch?.hfId,
    staleTime: 0,
  });

  const handlePageChange = (page: number, pageSize?: number) => {
    const newPage = Math.max(1, page);
    setPagination((prev) => ({
      current: newPage,
      pageSize: pageSize ?? prev.pageSize,
    }));
    refetch();
    walletRefetch()
  };

  const {
    data: banks,
    isPending: banksLoading,
    isError: banksError,
  } = useQuery<IBanksResponse>({
    queryKey: ["banks"],
    queryFn: async () => await getBanks(),
    staleTime: 1000 * 60 * 60,
  });

  const {
    data: allMetrics,
    isPending: allMetricsIsLoading,
    isError: allMetricsError,
    refetch: refetchMetrics,
  } = useQuery<MetricsResponse>({
    queryKey: ["all-metrics", branch?.hfId, timeRange],
    queryFn: async () => {
      if (!branch?.hfId) throw new Error("No branch ID available");
      return await getAllTransactionsMetrics(branch.hfId, 1, 1000);
    },
    enabled: !!branch?.hfId,
    staleTime: 1000 * 60 * 60,
  });

  const filteredMetrics = allMetrics?.results ? allMetrics.results.filter((item) => {
    const date = new Date(item.timestamp);
    const now = new Date();
    switch (timeRange) {
      case "Daily":
        return date.toDateString() === now.toDateString();
      case "Weekly":
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
        return date >= startOfWeek && date <= now;
      case "Monthly":
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      case "Yearly":
        return date.getFullYear() === now.getFullYear();
      default:
        return true;
    }
  }) : [];

  const validateBankAccountMutate = (params: { account_number: string; bank_code: string }) => {
    console.log("validateBankAccount called with:", params);
    return validateBankAccount(params.account_number, params.bank_code)
      .then((response) => {
        console.log("validateBankAccount direct response:", response);
        if (!response || typeof response !== "object") {
          throw new Error("Invalid response from validateBankAccount");
        }
        return response;
      })
      .catch((error) => {
        console.log("validateBankAccount direct error:", error);
        throw error;
      });
  };

  const { mutate: triggerValidate, isPending: validating, error: validateError } = useMutation<
    ValidateBankAccountResponse,
    Error,
    { account_number: string; bank_code: string }
  >({
    mutationFn: validateBankAccountMutate,
    onSuccess: (data) => {
      console.log("validateBankAccount onSuccess:", data);
      setBankValidationData(data);
      if (data.status) {
        const accountName = data?.data?.account_name || "";
        setAddBankFormData((prev) => ({ ...prev, account_name: accountName }));
        toast.success(data.message || "Account validated successfully");
      }
      else {
        return toast.error("bank validation failed")
      }
    },
    onError: (error) => {
      console.log("validateBankAccount onError:", error);
      setAddBankFormData((prev) => ({ ...prev, account_name: "" }));
      toast.error(`Validation failed: ${error.message}`);
    },
  });

  const handleFieldChange = (field: keyof IAddBankFormData, value: string | number | boolean) => {
    const updatedFormData = { ...addBankFormData, [field]: value };
    setAddBankFormData(updatedFormData);
    if (field === "account_number" || field === "bank_code") {
      if (updatedFormData.account_number && updatedFormData.bank_code && /^\d{10}$/.test(updatedFormData.account_number)) {
        triggerValidate({ account_number: updatedFormData.account_number, bank_code: updatedFormData.bank_code });
      } else {
        setAddBankFormData((prev) => ({ ...prev, account_name: "" }));
      }
    }
  };

  const { mutate: addBankMutate, isPending: addBankIsPending } = useMutation<IAddBankResponse>({
    mutationFn: async () => {
      const bankName = banks?.results.find((bank) => bank.bank_code === addBankFormData.bank_code);
      console.log(bankName);

      if (!branch?.hfId) throw new Error("No branch ID available");
      return await addBusinessBankAccount(branch.hfId, {
        account_name: addBankFormData.account_name,
        account_number: addBankFormData.account_number,
        bank: bankName?.id,
        active: false,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["business-bank-accounts", branch?.hfId] });
      toast.success("Bank account added successfully");
      navigate(-1);
    },
    onError: (error: any) => {
      let errorMessage = "Failed to add bank account.";
      if (error.response?.data?.errors) errorMessage = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.map((err: any) => err.detail).join(", ")
        : error.response.data.errors;
      toast.error(errorMessage);
    },
  });

  const { mutate: updateBankMutate, isPending: updateBankIsPending } = useMutation<UpdateBankAccountResponse>({
    mutationFn: async () => {
      if (!branch?.hfId || !selectedBank?.id) throw new Error("No branch or selected bank ID available");
      const payload = {
        account_name: addBankFormData.account_name,
        account_number: addBankFormData.account_number,
        bank_name: banks?.results.find((bank) => bank.bank_code === addBankFormData.bank_code)?.name || "",
        active: addBankFormData.active,
      };
      return await updateBusinessBankAccount(branch.hfId, selectedBank.id, payload, { format: "json" });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["business-bank-accounts", branch?.hfId] });
      toast.success("Bank account updated successfully");
      navigate(-1);
    },
    onError: (error: any) => {
      let errorMessage = error.message;
      if (error.response?.data?.errors) errorMessage = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.map((err: any) => err.detail).join(", ")
        : error.response.data.errors;
      toast.error(errorMessage);
    },
  });

  const { mutate: withdrawRequestMutate, isPending: withdrawRequestIsPending } = useMutation<WithdrawRequestResponse>({
    mutationFn: async () => {
      if (!withdrawFormData.business_bank_account || withdrawFormData.amount <= 0) throw new Error("Bank account and a valid amount are required");
      return await withdrawRequest(
        { business_bank_account: withdrawFormData.business_bank_account, amount: withdrawFormData.amount },
        { format: "json" }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["business-wallet", branch?.hfId] });
      toast.success("Withdrawal request processed successfully");
      closeConfirmModal();
      navigate(-1);
      if (withdrawFormData.saveAsDefault) {
        if (location.pathname.includes("business")) {
          if (!branch || withdrawFormData.id === null) return
          return await updateBusinessBankAccount(branch.hfId, withdrawFormData.id, { active: true }, { format: "json" });
        }
      }
    },
    onError: (error: any) => {
      let errorMessage = "Failed to process withdrawal request.";
      if (error.response?.data?.errors) errorMessage = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.map((err: any) => err.detail).join(", ")
        : error.response.data.errors;
      toast.error(errorMessage);
    },
  });

  const { mutate: deleteBankMutate, isPending: deleteBankIsPending } = useMutation({
    mutationFn: async () => {
      if (!branch?.hfId || !selectedBank?.id) throw new Error("No branch or selected bank ID available");
      return await deleteBusinessBankAccount(branch.hfId, selectedBank.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["business-bank-accounts", branch?.hfId] });
      toast.success("Bank account deleted successfully");
      setSelectedBank(null);
      closeDeleteBank();
    },
    onError: (error: any) => {
      let errorMessage = "Failed to delete bank account.";
      if (error.response?.data?.errors) errorMessage = Array.isArray(error.response.data.errors)
        ? error.response.data.errors.map((err: any) => err.detail).join(", ")
        : error.response.data.errors;
      toast.error(errorMessage);
    },
  });

  const toggleBusinessBalanceVisibility = () => {
    setIsBusinessBalanceVisible((prev) => !prev);
  };

  const getBusinessFormattedBalance = () => {
    if (!businessWallet?.balance) return "₦0.00";
    if (!isBusinessBalanceVisible) return "****";
    return currencyFormatter().format(Number(businessWallet.balance));
  };

  const currencyFormatter = () => new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", currencyDisplay: "narrowSymbol" });

  const handleAddBank = () => { if (!isFormComplete()) { toast.error("Please complete all bank account fields"); return; } addBankMutate(); };
  const handleUpdateBank = () => { if (!isFormComplete()) { toast.error("Please complete all bank account fields"); return; } updateBankMutate(); };
  const handleWithdrawRequest = () => withdrawRequestMutate();
  const handleDeleteBank = () => { if (!selectedBank) { toast.error("No bank selected"); return; } deleteBankMutate(); };

  const isFormComplete = () => addBankFormData.account_name && addBankFormData.account_number && addBankFormData.bank_code;
  const isWithdrawFormComplete = () => withdrawFormData.business_bank_account !== null && withdrawFormData.amount > 0;

  const handleRefreshBusinessWallet = async () => {
    try {
      await queryClient.invalidateQueries({ queryKey: ["business-wallet", branch?.hfId], refetchType: "active" });
      toast.success("Wallet refreshed");
    } catch (err) {
      toast.error("Failed to refresh wallet");
    }
  };

  const totalInflow = filteredMetrics.reduce((sum, item) => sum + (Number(item.inflow) || 0), 0);
  const totalOutflow = filteredMetrics.reduce((sum, item) => sum + (Number(item.outflow) || 0), 0);

  return {
    businessWallet,
    businessWalletError,
    validateError,
    validating,
    isLoading: businessWalletLoading || businessBankAccountLoading || banksLoading || addBankIsPending || updateBankIsPending || withdrawRequestIsPending || deleteBankIsPending,
    businessBankAccount,
    businessBankAccountLoading,
    banks: banks?.results || [],
    banksLoading,
    banksError,
    addBankFormData,
    setAddBankFormData,
    withdrawFormData,
    setWithdrawFormData,
    isWithdrawFormComplete,
    handleWithdrawRequest,
    isConfirmModalOpen,
    openConfirmModal,
    closeConfirmModal,
    branch,
    selectedBank,
    isDeleteBankOpen,
    openDeleteBank,
    closeDeleteBank,
    setSelectedBank,
    currencyFormatter,
    navigate,
    handleFieldChange,
    isFormComplete,
    handleAddBank,
    handleUpdateBank,
    handleDeleteBank,
    handleRefreshBusinessWallet,
    pagination,
    handlePageChange,
    bankValidationData,
    allMetrics,
    allMetricsIsLoading,
    allMetricsError,
    refetchMetrics,
    timeRange,
    setTimeRange,
    filteredMetrics,
    totalInflow,
    totalOutflow,
    businessWalletTransactions,
    getBusinessFormattedBalance,
    isBusinessBalanceVisible,
    toggleBusinessBalanceVisibility
  };
}